import React, { Fragment, useEffect, useState } from "react";

import { ReactComponent as EyeIcon } from "assets/images/common/svgs/eyeIconTable.svg";
import { useNavigate } from "react-router-dom";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import { UserFiltersDTO } from "utils/helpers/models/common/user-filters.dto";
import Pagination from "components/particles/table/pagination";
import DataNotFound from "components/particles/table/data-not-found";
import { useSelector } from "react-redux";
import { TICKET_STATUSES } from "utils/helpers/enums/shared.enum";
import useHostManagement from "containers/host-management/useHooks";
import ConfirmationModal from "../component/ConfirmationModal";

const CancelEventsListing: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const { getCancelEventsTicketsListing, approveCancelEventTicket } =
    useHostManagement();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [filters, setFilters] = useState<UserFiltersDTO>(new UserFiltersDTO());

  const [totalElements, setTotalElements] = useState<number>(0);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showApproveConfirmation, setShowApproveConfirmation] =
    useState<boolean>(false);

  const columns: string[] = [
    "Event Title",
    "Host Name",
    "Reason",
    "Entry Fee",
    "Penalty Amount",
    "Status",
    "Created Date",
    "Action",
  ];

  const CANCELLATION_STATUSES = ["pending", "approved", "rejected"];

  const onPageChange = (pageInfo: { selected: number; limit: number }) => {
    const { selected: page, limit } = pageInfo;
    filters.size = limit;
    filters.page = page + 1;
    setFilters({ ...filters });
    getTicketListing(filters);
  };

  const getTicketListing = (filters: UserFiltersDTO) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
      page: filters.page - 1,
    };
    getCancelEventsTicketsListing(
      setData,
      queryParams,
      setFilters,
      setTotalElements
    );
  };

  useEffect(() => {
    getTicketListing(filters);
  }, []);

  const handleChangeFilter = (event: any) => {
    const {
      target: { name, value },
    } = event;
    setFilters({ ...filters, [name]: value });
  };

  const resetFilters = () => {
    const model = new UserFiltersDTO();
    for (let key in model) {
      filters[key] = model[key];
    }

    setFilters({ ...filters, status: undefined });
    getTicketListing(filters);
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return "0";
    return `$${amount.toFixed(2)}`;
  };

  const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleOpenModal = (ticket: any) => {
    console.log("handleOpenModal called with ticket:", ticket);
    console.log("Current modal state - isModalOpen:", isModalOpen);
    if (ticket) {
      setSelectedTicket(ticket);
      setIsModalOpen(true);
      setShowApproveConfirmation(false);
      console.log("Modal should be open now");
    } else {
      console.error("Ticket is null or undefined");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setShowApproveConfirmation(false);
    setSelectedTicket(null);
  };

  const handleApprove = () => {
    setShowApproveConfirmation(true);
  };

  const handleReject = async () => {
    const result = await confirmationPopup(
      "Are you sure you want to reject this cancellation request?"
    );
    if (result.isConfirmed) {
      // TODO: Implement reject API call
      console.log("Reject ticket:", selectedTicket?.id);
      handleCloseModal();
      getTicketListing(filters);
    }
  };

  const handleApproveWithPenalty = async (adminNotes: string) => {
    if (!selectedTicket?.id) return;

    const body = {
      applyPenalty: true,
      adminNotes: adminNotes || "",
    };

    await approveCancelEventTicket(selectedTicket.id, body);
    handleCloseModal();
    getTicketListing(filters);
  };

  const handleApproveWithoutPenalty = async (adminNotes: string) => {
    if (!selectedTicket?.id) return;

    const body = {
      applyPenalty: false,
      adminNotes: adminNotes || "",
    };

    await approveCancelEventTicket(selectedTicket.id, body);
    handleCloseModal();
    getTicketListing(filters);
  };

  return (
    <div className="">
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold mb-6">
        Cancel Events
      </h1>

      <div className="bg-coolLightGrayBg border  border-borderLightGray p-4 rounded-lg w-full my-6">
        <div className="w-full overflow-x-auto scrollbar-hidden">
          <div className="flex flex-wrap sm:flex-nowrap lg:justify-end items-center gap-3">
            {/* Status  */}
            <select
              value={filters.status || ""}
              onChange={handleChangeFilter}
              name="status"
              className="px-3 border border-lightGrayBorder text-primaryText outline-none rounded-lg text-sm bg-white h-[46px] w-full sm:w-auto min-w-[140px]"
            >
              <option value="">All Status</option>
              {Object.values(TICKET_STATUSES).map((status, index) => {
                return (
                  <option value={status} key={index}>
                    {status
                      .split("_")
                      .map(
                        (word: string) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase()
                      )
                      .join(" ")}
                  </option>
                );
              })}
            </select>

            {/* Apply Filters Button */}
            <button
              onClick={() => getTicketListing(filters)}
              className="px-4 py-2 bg-darkTealBlue text-white rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Apply Filters
            </button>

            {/* Clear Filters Button */}
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-techGlowBg text-primaryText rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Clear Filter
            </button>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <div className="bg-white rounded-t-lg  border border-mediumGrayBorder overflow-x-auto">
          <table className="min-w-full divide-y divide-mediumGrayBorder">
            {/* Table Header */}
            <thead className="bg-white">
              <tr className="text-left">
                {columns.map((column, index) => {
                  return (
                    <th
                      className="px-4 py-4 text-sm font-bold text-left text-primaryText whitespace-nowrap"
                      key={index}
                    >
                      {column}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-mediumGrayBorder">
              {data.map((ticket: any, index) => {
                const hostName = ticket?.host?.user
                  ? `${ticket.host.user.firstName || ""} ${
                      ticket.host.user.lastName || ""
                    }`.trim() || ticket?.host?.businessName
                  : ticket?.host?.businessName || "-";

                return (
                  <tr key={ticket.id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                      {ticket?.event?.title || "-"}
                    </td>
                    <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                      {hostName}
                    </td>
                    <td className="px-4 py-4 text-sm text-primaryText whitespace-nowrap">
                      <div className="max-w-xs" title={ticket?.reason || ""}>
                        {truncateText(ticket?.reason, 50)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                      {formatCurrency(ticket?.event?.entryFee)}
                    </td>
                    <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                      {ticket?.status === "pending"
                        ? "-"
                        : formatCurrency(ticket?.penaltyAmount)}
                    </td>
                    <td className="px-4 py-4 text-left whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          ticket?.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : ticket?.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : ticket?.status === "rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {ticket?.status
                          ? ticket.status
                              .split("_")
                              .map(
                                (word: string) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase()
                              )
                              .join(" ")
                          : ""}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-primaryText whitespace-nowrap">
                      {ticket.createdAt
                        ? new Date(ticket.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "N/A"}
                    </td>
                    <td className="px-4 py-4 text-left whitespace-nowrap w-[124px]">
                      <div className="flex justify-start gap-2">
                        <button
                          onClick={() => {
                            console.log("Eye icon clicked, ticket:", ticket);
                            handleOpenModal(ticket);
                          }}
                          className="cursor-pointer"
                          type="button"
                          aria-label="View details"
                        >
                          <EyeIcon className="cursor-pointer" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Fragment>
          <DataNotFound show={!isLoading && !data.length} />
          <Pagination
            count={totalElements}
            page={filters.page}
            limit={filters.limit}
            onPageChange={onPageChange}
          />
        </Fragment>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        ticket={selectedTicket}
        onApprove={handleApprove}
        onReject={handleReject}
        showApproveConfirmation={showApproveConfirmation}
        onApproveWithPenalty={handleApproveWithPenalty}
        onApproveWithoutPenalty={handleApproveWithoutPenalty}
      />
    </div>
  );
};

export default CancelEventsListing;
